import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { Package, Search, Phone, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import DuplicateBadge from '../components/DuplicateBadge';
import { formatSize } from '../utils/variantUtils';

const UnassignedOrders = () => {
    const { t, i18n } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchUnassigned = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/orders', { params: { unassigned: 'true' } });
            // The res.data is the axios response payload, which is { success, data, pagination }
            if (res.data && res.data.success) {
                setOrders(res.data.data);
            } else {
                setOrders(Array.isArray(res.data) ? res.data : []);
            }
        } catch (err) {
            console.error("Failed to fetch unassigned orders", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnassigned();
    }, [fetchUnassigned]);

    const filteredOrders = orders.filter(o =>
        o.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.productName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="px-4 py-6 md:p-8 w-full max-w-7xl mx-auto space-y-6 md:space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">{t('unassigned_queue.title')}</h1>
                    <p className="text-gray-500 font-medium mt-1 uppercase tracking-widest text-[9px] md:text-[10px]">{t('unassigned_queue.subtitle')}</p>
                </div>
                <div className="bg-amber-50 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl border border-amber-100 flex items-center gap-2 self-start md:self-auto">
                    <AlertCircle size={14} className="text-amber-500 md:w-4 md:h-4" />
                    <span className="text-[9px] md:text-[10px] font-black text-amber-900 uppercase tracking-widest">{t('unassigned_queue.stats.pending', { count: orders.length })}</span>
                </div>
            </div>

            <div className="bg-white p-3 md:p-4 rounded-3xl border border-gray-100 shadow-sm flex items-center gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder={t('unassigned_queue.placeholders.search')}
                        className="w-full pl-11 md:pl-12 pr-4 py-2.5 md:py-3 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Desktop View */}
            <div className="hidden md:block bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-gray-50">
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.order')}</th>
                                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.customer')}</th>
                                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.product')}</th>
                                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.imported_at')}</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">{t('unassigned_queue.table.status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.loading')}</p>
                                    </td>
                                </tr>
                            ) : filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <Package size={40} className="mx-auto mb-4 text-gray-200" />
                                        <p className="text-gray-500 font-bold">{t('unassigned_queue.table.empty')}</p>
                                    </td>
                                </tr>
                            ) : filteredOrders.map((order) => (
                                <tr key={order._id} className="group hover:bg-gray-50/50 transition-colors">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2">
                                            <p className="font-black text-gray-900">#{order.orderId}</p>
                                            <DuplicateBadge order={order} />
                                        </div>
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{order.source || 'Sheet'}</span>
                                    </td>
                                    <td className="px-6 py-6">
                                        <p className="font-bold text-gray-900">{order.name}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <Phone size={12} className="text-gray-400" />
                                            <p className="text-xs font-bold text-gray-500">{order.phone}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 overflow-hidden border border-gray-100/50 flex-shrink-0">
                                                {order.productImage ? (
                                                    <img
                                                        src={order.productImage}
                                                        alt={order.productName}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { e.target.parentElement.innerHTML = '<div class="text-gray-400"><Package size={20} /></div>'; }}
                                                    />
                                                ) : (
                                                    <Package size={20} />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900 line-clamp-1">{order.productName}</p>
                                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mt-0.5"><bdi>{typeof order.variant === 'object' ? [order.variant.color, formatSize(order.variant.size)].filter(Boolean).join(" - ") : (formatSize(order.variant) || t('unassigned_queue.table.no_variant'))}</bdi></p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center gap-2">
                                            <Calendar size={14} className="text-gray-400" />
                                            <p className="text-xs font-bold text-gray-600">
                                                {formatDate(order.createdAt, i18n.language)}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-[9px] font-black uppercase tracking-widest border border-amber-200">
                                            {t('unassigned_queue.table.no_agent')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
                {loading ? (
                    <div className="bg-white rounded-3xl p-12 border border-gray-100 text-center shadow-sm">
                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-3" />
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-relaxed">{t('unassigned_queue.table.loading')}</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 border border-gray-100 text-center shadow-sm">
                        <Package size={40} className="mx-auto mb-4 text-gray-200" />
                        <p className="text-gray-500 font-bold">{t('unassigned_queue.table.empty')}</p>
                    </div>
                ) : filteredOrders.map((order) => (
                    <div key={order._id} className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm space-y-4">
                        <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="font-black text-gray-900 text-sm truncate">#{order.orderId}</p>
                                    <DuplicateBadge order={order} />
                                </div>
                                <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">{order.source || 'Sheet'}</span>
                            </div>
                            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[8px] font-black uppercase tracking-widest border border-amber-200 shrink-0">
                                {t('unassigned_queue.table.no_agent')}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-1">
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.customer')}</p>
                                <p className="font-bold text-gray-900 text-[13px]">{order.name}</p>
                                <div className="flex items-center gap-1.5">
                                    <Phone size={10} className="text-gray-400" />
                                    <p className="text-[11px] font-bold text-gray-500">{order.phone}</p>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('unassigned_queue.table.imported_at')}</p>
                                <div className="flex items-center gap-1.5 pt-0.5">
                                    <Calendar size={10} className="text-gray-400" />
                                    <p className="text-[11px] font-bold text-gray-600">
                                        {formatDate(order.createdAt, i18n.language)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-3 border-t border-gray-50">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">{t('unassigned_queue.table.product')}</p>
                            <div className="flex items-center gap-3 bg-gray-50/50 p-2 rounded-2xl border border-gray-100/50">
                                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-gray-400 overflow-hidden border border-gray-100 flex-shrink-0">
                                    {order.productImage ? (
                                        <img
                                            src={order.productImage}
                                            alt={order.productName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.parentElement.innerHTML = '<div class="text-gray-400"><Package size={20} /></div>'; }}
                                        />
                                    ) : (
                                        <Package size={20} />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-gray-900 text-xs truncate leading-tight">{order.productName}</p>
                                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-1">
                                        <bdi>{typeof order.variant === 'object' ? [order.variant.color, formatSize(order.variant.size)].filter(Boolean).join(" - ") : (formatSize(order.variant) || t('unassigned_queue.table.no_variant'))}</bdi>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UnassignedOrders;
